import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { doc, getDoc, collection, query, orderBy, limit, getDocs } from "firebase/firestore";
import { db } from "../firebase/config";

const BlogDetail = () => {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [recent, setRecent] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const snap = await getDoc(doc(db, "blogs", id));
        if (snap.exists()) setBlog({ id: snap.id, ...snap.data() });
        const rQ = query(collection(db, "blogs"), orderBy("createdAt", "desc"), limit(5));
        const rSnap = await getDocs(rQ);
        setRecent(rSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((b) => b.id !== id));
        const catSnap = await getDocs(collection(db, "blogCategories"));
        setCategories(catSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {}
      setLoading(false);
    };
    fetch();
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="w-10 h-10 border-4 border-[#e84fa5] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <p className="text-xl text-gray-400 mb-4">Post not found.</p>
        <Link to="/blog" className="text-[#e84fa5] hover:underline">← Back to Blog</Link>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen" style={{ fontFamily: "Montserrat, sans-serif" }}>
      <div className="max-w-[1200px] mx-auto px-6 py-10">
        <div className="text-[12px] text-gray-400 mb-6 flex items-center gap-2">
          <Link to="/" className="hover:text-[#e84fa5]">Home</Link>
          <span>/</span>
          <Link to="/blog" className="hover:text-[#e84fa5]">Blog</Link>
          <span>/</span>
          <span className="text-gray-700">{blog.title}</span>
        </div>

        <div className="flex flex-col lg:flex-row gap-12">
          {/* Main */}
          <article className="flex-1">
            {blog.coverImage && (
              <div className="w-full aspect-[16/7] overflow-hidden rounded-xl mb-8 bg-gray-100">
                <img src={blog.coverImage} alt={blog.title} className="w-full h-full object-cover" />
              </div>
            )}

            <div className="flex items-center gap-3 mb-4">
              {blog.category && (
                <span className="px-3 py-1 bg-pink-50 text-[#e84fa5] text-[10px] font-bold uppercase rounded-full">
                  {blog.category}
                </span>
              )}
              <span className="text-[12px] text-gray-400">
                {blog.createdAt?.toDate ? blog.createdAt.toDate().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : ""}
              </span>
            </div>

            <h1 className="text-3xl font-extrabold text-black leading-tight mb-6">{blog.title}</h1>

            {blog.excerpt && (
              <p className="text-[15px] text-gray-500 italic border-l-4 border-[#e84fa5] pl-4 mb-8 leading-relaxed">
                {blog.excerpt}
              </p>
            )}

            {/* Blog content - rendered as HTML from rich editor */}
            <div
              className="prose max-w-none text-[14px] text-gray-700 leading-relaxed blog-content"
              dangerouslySetInnerHTML={{ __html: blog.content || "" }}
            />

            <div className="mt-10 pt-6 border-t">
              <Link to="/blog" className="text-[#e84fa5] text-[13px] font-semibold hover:underline">
                ← Back to Blog
              </Link>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="w-full lg:w-[260px] space-y-8">
            <div className="bg-gray-50 rounded-xl p-5">
              <h3 className="text-[13px] font-extrabold uppercase mb-4">Categories</h3>
              <ul className="space-y-2">
                {categories.map((cat) => (
                  <li key={cat.id}>
                    <Link to={`/blog?cat=${cat.name}`} className="text-[13px] text-gray-600 hover:text-[#e84fa5] transition">
                      {cat.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-5">
              <h3 className="text-[13px] font-extrabold uppercase mb-4">Recent Posts</h3>
              <ul className="space-y-4">
                {recent.map((b) => (
                  <li key={b.id} className="flex gap-3">
                    {b.coverImage && (
                      <img src={b.coverImage} alt="" className="w-14 h-14 object-cover rounded shrink-0" />
                    )}
                    <div>
                      <Link to={`/blog/${b.id}`} className="text-[12px] font-semibold text-black hover:text-[#e84fa5] transition leading-tight">
                        {b.title}
                      </Link>
                      <p className="text-[10px] text-gray-400 mt-1">
                        {b.createdAt?.toDate ? b.createdAt.toDate().toLocaleDateString() : ""}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default BlogDetail;
